import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, Users, HardDrive, CreditCard, ArrowRight, Minus, Plus, ChevronDown } from 'lucide-react';
import api from '../utils/api';


const PricingSignup = () => {
  const [searchParams] = useSearchParams();
  const canceled = searchParams.get('canceled');
  
  const [step, setStep] = useState(1); // 1: Plan selection, 2: Account details
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [seats, setSeats] = useState(1);
  const [pricing, setPricing] = useState(null);
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Account form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    teamName: ''
  });

  // Fetch pricing info on mount
  useEffect(() => {
    fetchPricing();
  }, []);

  // Recalculate when seats or billing cycle changes
  useEffect(() => {
    if (pricing) {
      calculatePrice();
    }
  }, [seats, billingCycle, pricing]);

  const fetchPricing = async () => {
    try {
      const response = await api.get(`/billing/pricing`);
      if (response.data.success) {
        setPricing(response.data.pricing);
      }
    } catch (err) {
      console.error('Failed to fetch pricing:', err);
    }
  };

  const calculatePrice = async () => {
    try {
      const response = await api.post(`/billing/calculate`, {
        seats,
        billing_cycle: billingCycle
      });
      if (response.data.success) {
        setCalculatedPrice(response.data.pricing);
      }
    } catch (err) {
      console.error('Failed to calculate price:', err);
    }
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return false;
    }
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) {
      setError('Valid email is required');
      return false;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleProceedToCheckout = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await api.post(`/billing/checkout/subscription`, {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        team_name: formData.teamName || `${formData.name}'s Team`,
        seats,
        billing_cycle: billingCycle,
        origin_url: window.location.origin
      });
      
      if (response.data.success && response.data.checkout_url) {
        // Store user/team info for after checkout
        localStorage.setItem('pending_signup', JSON.stringify({
          user_id: response.data.user_id,
          team_id: response.data.team_id,
          email: formData.email
        }));
        
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create checkout session');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Pricing card data for exact line parity
  const pricingCards = [
    {
      id: 'monthly',
      line1: 'Monthly',
      line2: 'Pay as you go',
      line3: pricing ? `$${(pricing.base_monthly + (seats > 1 ? (seats - 1) * pricing.seat_monthly : 0)).toFixed(2)} / mo` : '-- / mo',
      line4: pricing ? `$${pricing.base_monthly} first user` : '-- first user',
      line5: pricing ? `$${pricing.seat_monthly} per additional seat` : '-- per additional seat',
      line6: 'Onboarding and success support included',
      buttonLabel: 'Select Monthly',
      buttonClass: 'bg-gradient-to-r from-purple-500 to-purple-400',
      borderClass: 'border-purple-500',
      ringClass: 'ring-purple-500/50',
      badge: null
    },
    {
      id: 'annual',
      line1: 'Annual',
      line2: 'Best value',
      line3: pricing ? `$${((pricing.base_annual + (seats > 1 ? (seats - 1) * pricing.seat_annual : 0)) / 12).toFixed(2)} / mo` : '-- / mo',
      line4: pricing ? `$${(pricing.base_annual / 12).toFixed(2)} first user` : '-- first user',
      line5: pricing ? `$${(pricing.seat_annual / 12).toFixed(2)} per additional seat` : '-- per additional seat',
      line6: 'Onboarding and success support included',
      buttonLabel: 'Select Annual',
      buttonClass: 'bg-gradient-to-r from-purple-400 to-pink-500',
      borderClass: 'border-[#EC499A]',
      ringClass: 'ring-[#EC499A]/50',
      badge: 'Save 22%'
    }
  ];

  // Testimonials
  const testimonials = [
    { quote: "Major lever pull for us. Athena is awesome.", author: "B Cole" },
    { quote: "I'm getting 10+ Hours back a month!", author: "G MacCleod" },
    { quote: "The Client Portal has revolutionized my agency", author: "H Smith" },
    { quote: "My clients Love it. My team loves it. I love it.", author: "J Bristol" },
    { quote: "The feedback/Deliverable system is brilliant!", author: "C Buchanan" }
  ];

  // Testimonial rotation state
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  // Rotate testimonials every 6 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  // What's included items
  const includedFeatures = [
    'White-glove onboarding',
    'Dedicated Customer Success Manager',
    'Unlimited clients and projects',
    'Workflow automation and routing',
    'Client portal access',
    'Email integrations'
  ];

  // Trust section bullets
  const trustBullets = [
    'Your system is prepared before onboarding',
    'You launch with real workflows, not templates',
    'A success manager ensures Athena fits your business',
    'Most teams are live and delivering within days'
  ];

  // FAQs
  const faqs = [
    {
      question: "Do you offer a free trial?",
      answer: "No. Athena is designed to deliver value quickly through guided setup and onboarding. You're paying for a working delivery system, not access to unfinished software."
    },
    {
      question: "How fast can we be up and running?",
      answer: "Most teams are live and delivering client work within days. Your system is prepared before onboarding, so setup focuses on launching real workflows, not learning the software."
    },
    {
      question: "What happens after we sign up?",
      answer: "After signup, you schedule an onboarding session at a time that works for you. Before that call, we prepare Athena around how your business operates. On the call, we finalize setup, train your team, and launch."
    },
    {
      question: "Is onboarding really included?",
      answer: "Yes. Every plan includes white-glove onboarding. You are not expected to configure Athena on your own."
    },
    {
      question: "Do we get a dedicated Customer Success Manager?",
      answer: "Yes. Every account includes access to a dedicated Customer Success Manager who helps optimize workflows, automations, and integrations as your business grows."
    },
    {
      question: "Is Athena just another project management tool?",
      answer: "No. Athena acts as a project manager partner, not just a place to track tasks. It runs coordination, handoffs, and follow-ups so delivery doesn't depend on manual effort."
    },
    {
      question: "How many clients or projects can we have?",
      answer: "Unlimited. Athena does not cap clients or projects, so your delivery system can scale without artificial limits."
    },
    {
      question: "What if my team grows or shrinks?",
      answer: "You can add or remove team members at any time. You only pay for the people actively doing the work."
    },
    {
      question: "Is Athena suitable for larger teams?",
      answer: "Yes. Athena works well for small teams and scales to support larger, more complex operations while keeping pricing simple."
    },
    {
      question: "Is there a contract?",
      answer: "No long-term contracts. You can cancel anytime. Annual plans renew yearly unless canceled."
    },
    {
      question: "What integrations are supported?",
      answer: "Athena integrates with email and other core systems to keep communication tied directly to work. Additional integrations can be configured during onboarding."
    },
    {
      question: "Is there a mobile app?",
      answer: "A mobile app is planned and coming soon. Athena is fully usable on desktop and optimized for running operations efficiently."
    },
    {
      question: "What if Athena isn't a good fit?",
      answer: "If Athena isn't right for your business, we'll help you transition cleanly. Our goal is long-term success, not locking you into software that doesn't serve you."
    }
  ];

  // FAQ open state
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="py-6 px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/images/athena-logo-wide.png" 
              alt="Athena" 
              className="h-14 w-auto"
            />
          </div>
          <a
            href="/"
            className="text-purple-300 hover:text-white transition-colors"
          >
            Already have an account? Sign in
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-12">
        {canceled && (
          <div className="mb-8 p-4 bg-yellow-500/20 border border-yellow-500/50 rounded-lg text-yellow-200 text-center">
            Your checkout was canceled. Feel free to try again when you&apos;re ready.
          </div>
        )}

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-12">
          {[1, 2].map((s) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-2 ${step >= s ? 'text-white' : 'text-gray-500'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${
                  step > s ? 'bg-[#EC499A]' : step === s ? 'bg-purple-500' : 'bg-gray-700'
                }`}>
                  {step > s ? <Check size={16} /> : s}
                </div>
                <span className="hidden sm:inline font-medium">
                  {s === 1 ? 'Choose Plan' : 'Account Details'}
                </span>
              </div>
              {s < 2 && <ArrowRight className="text-gray-600" size={20} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Plan Selection */}
        {step === 1 && (
          <div className="space-y-10">
            {/* Page Header - Updated Copy */}
            <div className="text-center">
              <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">Operate at a higher level with Athena</h1>
              <p className="text-xl text-purple-200">Every plan includes guided setup and a dedicated success manager so your delivery system is live fast.</p>
            </div>

            {/* What's Included Section - Above seat selector */}
            <div className="max-w-2xl mx-auto">
              <h3 className="text-center text-white text-xl font-semibold mb-6">What&apos;s included with every plan</h3>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-purple-500/20">
                <ul className="grid sm:grid-cols-2 gap-4">
                  {includedFeatures.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-purple-100">
                      <Check className="text-[#EC499A] flex-shrink-0" size={18} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Seat Selector with Helper Text - Right above pricing cards */}
            <div id="plan-selection" className="max-w-md mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-purple-500/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="text-purple-400" size={20} />
                    <span className="text-white font-medium">Team Members</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setSeats(Math.max(1, seats - 1))}
                      className="w-8 h-8 rounded-full bg-purple-500/30 text-white flex items-center justify-center hover:bg-purple-500/50 transition-colors disabled:opacity-50"
                      disabled={seats <= 1}
                    >
                      <Minus size={16} />
                    </button>
                    <span className="text-2xl font-bold text-white w-12 text-center">{seats}</span>
                    <button
                      onClick={() => setSeats(seats + 1)}
                      className="w-8 h-8 rounded-full bg-purple-500/30 text-white flex items-center justify-center hover:bg-purple-500/50 transition-colors"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>
              </div>
              {/* Helper Text */}
              <p className="text-center text-purple-300 text-sm mt-3">
                Only pay for the people doing the work. Scale seats up or down anytime.
              </p>
            </div>

            {/* Side by Side Pricing Cards - Exact 6-line parity */}
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {pricingCards.map((card) => (
                <div 
                  key={card.id}
                  onClick={() => setBillingCycle(card.id)}
                  className={`bg-white/10 backdrop-blur-sm rounded-2xl p-6 border-2 cursor-pointer transition-all relative flex flex-col ${
                    billingCycle === card.id 
                      ? `${card.borderClass} ring-2 ${card.ringClass}` 
                      : 'border-purple-500/30 hover:border-purple-500/60'
                  }`}
                  style={{ minHeight: '320px' }}
                >
                  {/* Badge - Position absolute, doesn't affect layout */}
                  {card.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                      <span className="bg-[#EC499A] text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                        {card.badge}
                      </span>
                    </div>
                  )}

                  {/* "Onboarding included" subtle badge */}
                  <div className="absolute top-3 right-3">
                    <span className="text-[10px] text-purple-300 bg-purple-500/20 px-2 py-0.5 rounded-full">
                      Onboarding included
                    </span>
                  </div>

                  {/* 6 Text Lines Container - Fixed structure */}
                  <div className="flex flex-col items-center text-center flex-grow" style={{ minHeight: '200px' }}>
                    {/* Line 1: Plan name */}
                    <h3 className="text-xl font-bold text-white mt-2" style={{ lineHeight: '1.5' }}>
                      {card.line1}
                    </h3>
                    
                    {/* Line 2: Subtitle */}
                    <p className="text-purple-200 text-sm" style={{ lineHeight: '1.5' }}>
                      {card.line2}
                    </p>
                    
                    {/* Line 3: Price - Slightly reduced emphasis */}
                    <p className="text-3xl font-semibold text-white mt-4" style={{ lineHeight: '1.5' }}>
                      {card.line3}
                    </p>
                    
                    {/* Line 4: First user price */}
                    <p className="text-sm text-purple-300 mt-2" style={{ lineHeight: '1.5' }}>
                      {card.line4}
                    </p>
                    
                    {/* Line 5: Additional seat price */}
                    <p className="text-sm text-purple-300" style={{ lineHeight: '1.5' }}>
                      {card.line5}
                    </p>
                    
                    {/* Line 6: Onboarding included - Increased emphasis */}
                    <p className="text-sm text-[#EC499A] font-semibold mt-3" style={{ lineHeight: '1.5' }}>
                      {card.line6}
                    </p>
                  </div>

                  {/* CTA Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setBillingCycle(card.id);
                      setStep(2);
                    }}
                    className={`w-full py-3 font-semibold rounded-xl transition-all mt-auto ${card.buttonClass} text-white hover:opacity-90`}
                  >
                    {card.buttonLabel}
                  </button>
                </div>
              ))}
            </div>

            {/* Cycling Testimonials Section */}
            <div className="max-w-4xl mx-auto py-8">
              <div className="relative h-24 flex items-center justify-center overflow-hidden">
                <div 
                  key={currentTestimonial}
                  className="absolute inset-0 flex items-center justify-center animate-fade-in"
                >
                  <div className="flex items-baseline gap-4 px-4">
                    <p className="text-2xl md:text-3xl font-light text-white italic text-center">
                      &ldquo;{testimonials[currentTestimonial].quote}&rdquo;
                    </p>
                    <span className="text-sm text-purple-400 whitespace-nowrap">
                      — {testimonials[currentTestimonial].author}
                    </span>
                  </div>
                </div>
              </div>
              {/* Dots indicator */}
              <div className="flex justify-center gap-2 mt-4">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentTestimonial(i)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      i === currentTestimonial ? 'bg-[#EC499A] w-4' : 'bg-purple-500/50'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Storage Section - Updated framing */}
            <div className="max-w-2xl mx-auto text-center">
              <h3 className="text-white text-lg font-semibold mb-2">Storage that grows with you</h3>
              <p className="text-purple-300 text-sm mb-4">
                Athena includes 20GB of storage to get you started. Add more anytime as your delivery volume grows.
              </p>
              <div className="flex items-center justify-center gap-6 text-purple-200 text-sm">
                <div className="flex items-center gap-2">
                  <HardDrive className="text-purple-400" size={16} />
                  <span>100GB: $20/mo</span>
                </div>
                <div className="flex items-center gap-2">
                  <HardDrive className="text-purple-400" size={16} />
                  <span>1TB: $50/mo</span>
                </div>
                <span className="text-purple-400 text-xs">(22% off with annual)</span>
              </div>
            </div>

            {/* Trust Section - New */}
            <div className="max-w-2xl mx-auto">
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-purple-500/20">
                <h3 className="text-white text-xl font-semibold mb-6 text-center">Why teams start with a paid plan</h3>
                <ul className="space-y-4">
                  {trustBullets.map((bullet, i) => (
                    <li key={i} className="flex items-start gap-3 text-purple-100">
                      <Check className="text-[#EC499A] flex-shrink-0 mt-0.5" size={18} />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Get Athena Now CTA - Above FAQs */}
            <div className="text-center">
              <button
                onClick={() => document.getElementById('plan-selection')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity text-lg"
              >
                Get Athena Now
              </button>
            </div>

            {/* FAQ Section */}
            <div className="max-w-3xl mx-auto">
              <h3 className="text-center text-white text-2xl font-semibold mb-8">Frequently Asked Questions</h3>
              <div className="space-y-3">
                {faqs.map((faq, index) => (
                  <div 
                    key={index}
                    className="bg-white/5 backdrop-blur-sm rounded-xl border border-purple-500/20 overflow-hidden"
                  >
                    <button
                      onClick={() => toggleFaq(index)}
                      className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                    >
                      <span className="text-white font-medium pr-4">{faq.question}</span>
                      <ChevronDown 
                        className={`text-purple-400 flex-shrink-0 transition-transform duration-200 ${
                          openFaq === index ? 'rotate-180' : ''
                        }`} 
                        size={20} 
                      />
                    </button>
                    {openFaq === index && (
                      <div className="px-6 pb-4">
                        <p className="text-purple-200 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Final CTA Section - Updated */}
            <div className="text-center pt-6">
              <h3 className="text-2xl font-bold text-white mb-4">
                Ready to run client delivery instead of managing it?
              </h3>
              <button
                onClick={() => document.getElementById('plan-selection')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity text-lg"
              >
                Start with Onboarding
              </button>
              <p className="text-purple-300 text-sm mt-4">
                Choose a plan, schedule onboarding, and launch your delivery system.
              </p>
            </div>
          </div>
        )}

        {/* Step 2: Account Details */}
        {step === 2 && (
          <div className="max-w-md mx-auto space-y-8">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-white mb-4">Create Your Account</h1>
              <p className="text-xl text-purple-200">Almost there! Set up your account details.</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-purple-500/30">
              {error && (
                <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-purple-200 text-sm font-medium mb-2">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 bg-white/10 border border-purple-500/30 rounded-lg text-white placeholder-purple-300/50 focus:outline-none focus:border-purple-500"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-purple-200 text-sm font-medium mb-2">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 bg-white/10 border border-purple-500/30 rounded-lg text-white placeholder-purple-300/50 focus:outline-none focus:border-purple-500"
                    placeholder="john@company.com"
                  />
                </div>

                <div>
                  <label className="block text-purple-200 text-sm font-medium mb-2">Team/Company Name (optional)</label>
                  <input
                    type="text"
                    name="teamName"
                    value={formData.teamName}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 bg-white/10 border border-purple-500/30 rounded-lg text-white placeholder-purple-300/50 focus:outline-none focus:border-purple-500"
                    placeholder="Acme Inc."
                  />
                </div>

                <div>
                  <label className="block text-purple-200 text-sm font-medium mb-2">Password</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 bg-white/10 border border-purple-500/30 rounded-lg text-white placeholder-purple-300/50 focus:outline-none focus:border-purple-500"
                    placeholder="••••••••"
                  />
                </div>

                <div>
                  <label className="block text-purple-200 text-sm font-medium mb-2">Confirm Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 bg-white/10 border border-purple-500/30 rounded-lg text-white placeholder-purple-300/50 focus:outline-none focus:border-purple-500"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              {/* Order Summary */}
              <div className="mt-6 p-4 bg-purple-500/20 rounded-lg">
                <h4 className="text-white font-semibold mb-2">Order Summary</h4>
                <div className="space-y-1 text-sm text-purple-200">
                  <div className="flex justify-between">
                    <span>Plan: Professional ({billingCycle})</span>
                    <span>{seats} seat{seats > 1 ? 's' : ''}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-white pt-2 border-t border-purple-500/30">
                    <span>Total</span>
                    <span>
                      {calculatedPrice && formatPrice(calculatedPrice.total)}
                      {billingCycle === 'annual' ? '/year' : '/month'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex gap-4">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 py-3 border border-purple-500/50 text-purple-200 font-medium rounded-lg hover:bg-purple-500/20 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleProceedToCheckout}
                  disabled={loading || !formData.name || !formData.email || !formData.password || formData.password !== formData.confirmPassword}
                  className="flex-1 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Processing...
                    </span>
                  ) : (
                    <>
                      <CreditCard size={18} /> Proceed to Payment
                    </>
                  )}
                </button>
              </div>
              
              {error && (
                <div className="mt-4 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200 text-center">
                  {error}
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default PricingSignup;
